import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { users } from '../models/users';
import { User } from '../types/User';
import { userRoles } from '../models/userRoles';
import { UserRole } from '../types/UserRole';
import { roles } from '../models/roles';
import { Role } from '../types/Role';
import { eq, and, inArray } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import bcrypt from "bcryptjs";
import { convertToAppError, AppError, AsyncResult, err, ok, tryCatchAsync } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';

/**
 * @group User
 * @summary User repository
 * 
 * <blockquote>
 * Provides data access methods for user management, including CRUD operations and user-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const userRepository = new UserRepository();
 * const user = await userRepository.getUserById('123');
 * ```
 * 
 */
export class UserRepository extends BaseRepository<User> {
  protected table = users;

  /**
   * Creates a new user with hashed password
   */
  async create(data: Partial<User>): AsyncResult<User, AppError> {
    if (!data.email || !data.password) {
      return err(convertToAppError('Email and password are required', DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.BAD_REQUEST));
    }

    const hashedPasswordResult = await this.hashPassword(data.password);
    if (hashedPasswordResult.isErr) {
      return err(convertToAppError('Password hashing error', DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
    }

    const hashedPassword = hashedPasswordResult.getSuccess();

    const email = data.email.toLowerCase().replace(/\s+/g, '');
    const insertData = {
      email,
      password: hashedPassword,
      emailVerified: data.emailVerified ?? false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    return await super.create(insertData);
  }

  /**
   * Creates a user if one doesn't already exist by email
   */
  async createIfNotExists(data: Partial<User>): AsyncResult<User, AppError> {
    if (!data.email) {
      return err(convertToAppError('Email is required', DATA_API_MESSAGES.ERRORS.BAD_REQUEST, HTTP_STATUS_NAMES.BAD_REQUEST));
    }

    const email = data.email.toLowerCase().replace(/\s+/g, '');
    const existingUserResult = await this.getUserByEmail(email);

    return existingUserResult.match({
      ok: async (user) => {
        if (user !== null) {
          return ok(user)
        }

        return await this.create(data);
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Get user roles by user ID
   */
  async getUserRoles(userId: string): AsyncResult<Role[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({ roles })
            .from(userRoles)
            .innerJoin(roles, eq(userRoles.role_id, roles.id))
            .where(eq(userRoles.user_id, userId))
        );

        return queryResult.match({
          ok: userRoles => ok(userRoles.map(ur => ur.roles)),
          err: (error) => err(error)});
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Add role to user
   */
  async addRoleToUser(userId: string, roleId: string): AsyncResult<UserRole, AppError> {
    return db.match({
      ok: async (database) => {
        const insertData = {
          user_id: userId,
          role_id: roleId,
          date_created: new Date(),
        };

        const queryResult = await retryableQuery(() =>
          database.insert(userRoles).values(insertData).returning()
        );

        return queryResult.match({
          ok: result => ok(result[0]),
          err: error => err(error),
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId: string, roleId: string): AsyncResult<boolean, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .delete(userRoles)
            .where(and(eq(userRoles.user_id, userId), eq(userRoles.role_id, roleId)))
            .returning()
        );

        return queryResult.match({
          ok: result => ok(result.length > 0),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get user by email
   * Normalizes email to lowercase before querying for case-insensitive lookup
   */
  async getUserByEmail(email: string): AsyncResult<User | null, AppError> {
    // Normalize email to lowercase for case-insensitive lookup
    const normalizedEmail = email.toLowerCase().trim();
    
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database.select().from(users).where(eq(users.email, normalizedEmail))
        );

        return queryResult.match({
          ok: rows => {
            if (!Array.isArray(rows)) {
                return err(convertToAppError("Unexpected result from getUserByEmail: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (!rows[0]) {
              return ok(null);
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Hash user password using bcrypt
   */
  async hashPassword(password: string): AsyncResult<string, AppError> {
    const saltRounds = Math.floor(Math.random() * 10) + 1;
    return await tryCatchAsync(() => bcrypt.hash(password, saltRounds));
  }

  /**
   * Compare plaintext and hashed password
   */
  async verifyPassword(password: string, hashedPassword: string): AsyncResult<boolean, AppError> {
    return await tryCatchAsync(() => bcrypt.compare(password, hashedPassword));
  }

  /**
   * Get users by their IDs
   * 
   * @param userIds - Array of user IDs to retrieve
   * @returns AsyncResult containing array of users or error
   */
  async getUsersByIds(userIds: string[]): AsyncResult<User[], AppError> {
    if (!userIds || userIds.length === 0) {
      return ok([]);
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select()
            .from(users)
            .where(inArray(users.id, userIds))
        );

        return queryResult.match({
          ok: (userRows) => ok(userRows),
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Get users linked to tenants associated with the specified user
   * 
   * This method retrieves all users who are members of:
   * 1. Tenants owned by the user (where tenants.user_id = userId)
   * 2. Tenants where the user is a member (via TenantUsers table)
   * 3. Tenants owned by members of tenants where the user is a member (transitive access)
   * 
   * This expanded scope allows tenant admins to see users from tenants where they are members,
   * and also from tenants owned by members of their member tenants. It uses TenantUserRepository
   * to get the user IDs, then fetches the full user records.
   * 
   * @param ownerUserId - The user ID
   * @returns AsyncResult containing array of users linked to associated tenants, or error
   * 
   * @example
   * ```typescript
   * const usersResult = await repository.getUsersLinkedToOwnedTenants('user-123');
   * if (usersResult.isOk()) {
   *   const users = usersResult.value;
   *   console.log(`Found ${users.length} users in associated tenant scope`);
   * }
   * ```
   */
  async getUsersLinkedToOwnedTenants(ownerUserId: string): AsyncResult<User[], AppError> {
    if (!ownerUserId) {
      return err(convertToAppError(
        'Owner user ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // Import TenantUserRepository here to avoid circular dependency
    const { TenantUserRepository } = await import('./TenantUserRepository');
    const tenantUserRepo = new TenantUserRepository();

    // Get user IDs linked to associated tenants (owned + member + transitive)
    const userIdsResult = await tenantUserRepo.getUsersLinkedToOwnedTenants(ownerUserId);
    if (userIdsResult.isErr) {
      return err(userIdsResult.getFail());
    }

    const userIds = userIdsResult.getSuccess();
    
    // If no users found, return empty array
    if (userIds.length === 0) {
      return ok([]);
    }

    // Fetch full user records
    return await this.getUsersByIds(userIds);
  }

}